import { useState, useEffect, useRef } from "react";
import AwesomeIcon from "../AwesomeIcon";
import {
  faCalendar,
  faUser,
  faCircleCheck,
  faCircleExclamation,
  faClipboardList,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import { isEmpty, truncate } from "lodash";
import { parse } from "best-effort-json-parser";

interface TimelineEvent {
  type: string;
  items: string[];
}

interface TimelineItem {
  date: string;
  events: TimelineEvent[];
}

interface Patient {
  id: string;
  fullName: string;
  gender: string;
  age: number;
  demographics: string[];
}

interface PatientData {
  date: string;
  patient: Patient;
  overview: string;
  timeline: TimelineItem[];
}

export default function PatientSummary({ message }: { message: string }) {
  // const [patientData.current, setPatientData.current] = useState<PatientData.current | null>(null);
  const patientData = useRef<PatientData | any>({
    date: "",
    patient: {
      id: "",
      fullName: "",
      gender: "",
      age: 0,
      demographics: [],
    },
    overview: "",
    timeline: [],
  });

  useEffect(() => {
    if (message.length > 0) {
      const jsonMatchMarkdown = message.match(/```(?:json)?\s*([\s\S]*)/i);
      const messageData = message.split("<<<PATIENT_SUMMARY_JSON>>>")[1];
      // let messageData = message;
      // if (jsonMatchMarkdown) {
      //   messageData = jsonMatchMarkdown[1];
      // }

      try {
        const parsedMessageData = parse(messageData);
        patientData.current = parsedMessageData;
      } catch (error) {
        console.error({ error });
      }
    }
  }, [message]);

  const [openItems, setOpenItems] = useState<string[]>(["timeline-20250408-0"]);
  if (patientData.current === null) {
    return (
      <div className="flex items-center justify-center my-2 mx-4">
        <AwesomeIcon name={faSpinner} size="lg" className="animate-spin" />
        <p className="text-md italic pl-4">Patient Summary is loading...</p>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original string if date is invalid
      }
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  // Generate a unique ID for each timeline item
  const generateTimelineId = (date: string, index: number) => {
    return `timeline-${date?.replace(/-/g, "")}-${index}`;
  };

  const toggleItem = (value: string) => {
    setOpenItems(
      openItems.includes(value)
        ? openItems.filter((item) => item !== value)
        : [...openItems, value]
    );
  };

  return (
    <div className="h-full bg-gray-400 dark:bg-gray-950 border-gray-300 rounded-lg rounded-tl-none text-gray-200">
      <div className="max-w-2xl mx-auto">
        <div className="dark:bg-gray-900 bg-gray-100 shadow-md overflow-hidden border-0 rounded-lg rounded-tl-none">
          {/* Header */}
          <div className="p-4 border-b border-gray-300 dark:border-gray-700 flex justify-between items-center bg-gray-600 dark:bg-gray-800">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-medium text-white">Clinical Summary</h1>
            </div>
            <span className="text-sm dark:text-gray-300 text-gray-100">
              {formatDate(patientData.current?.date)}
            </span>
          </div>

          {/* Patient Summary - Always visible */}
          <div className="p-4 border-b dark:border-gray-800 border-gray-300 flex items-center gap-3 border dark:border-gray-700">
            <div className="h-8 w-8 rounded-full dark:bg-gray-800 bg-gray-200 flex items-center justify-center">
              <AwesomeIcon name={faUser} className="h-5 w-5 text-[#FFA387]" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h2 className="text-lg font-medium dark:text-white text-gray-700 break-words">
                  {patientData.current?.patient?.fullName
                    ? truncate(patientData.current?.patient?.fullName, { length: 25 })
                    : "Patient"}
                </h2>
                <span className="text-md dark:text-gray-300 text-gray-800 dark:bg-gray-800 bg-gray-300 px-1.5 py-0.5 rounded">
                  {patientData.current?.patient?.age}Y{" "}
                  {patientData.current?.patient?.gender}
                </span>
              </div>
              <p
                className="text-sm dark:text-gray-300 text-gray-800 mt-1"
                dangerouslySetInnerHTML={{
                  __html: patientData.current?.patient?.demographics?.join(" • "),
                }}
              />
            </div>
          </div>

          {/* Clinical Overview - Always visible */}
          <div className="p-4 border dark:border-gray-800 border-gray-300">
            <div className="flex items-center gap-2 mb-3">
              <AwesomeIcon name={faCircleCheck} className="h-5 w-5 text-teal-400" />
              <h2 className="text-lg font-medium text-teal-400">Clinical Overview</h2>
            </div>
            <div
              className="text-md dark:text-gray-200 text-gray-600 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: patientData.current?.overview }}
            />
          </div>

          {/* Timeline Section Header */}
          <>
            {patientData.current?.timeline?.length > 0 && (
              <>
                <div className="px-4 py-3 border dark:border-gray-800 border-gray-300 flex items-center gap-2">
                  <AwesomeIcon name={faCalendar} className="h-5 w-5 text-teal-400" />
                  <h2 className="text-lg font-medium text-teal-400">
                    Timeline of Events
                  </h2>
                </div>

                <div className="w-full">
                  {patientData.current?.timeline?.map((timelineItem, index) => {
                    const dateId = generateTimelineId(timelineItem.date, index);
                    const formattedDate = formatDate(timelineItem.date);
                    const isOpen = openItems.includes(dateId);

                    return (
                      <div
                        key={dateId}
                        className="border dark:border-gray-800 border-gray-300"
                      >
                        <button
                          className="flex w-full items-center justify-between px-4 py-3 hover:bg-gray-400/50 dark:hover:bg-gray-800/50 dark:text-white text-gray-600"
                          onClick={() => toggleItem(dateId)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-base font-medium">{formattedDate}</span>
                            <span className="text-md ml-3 px-4 py-1 rounded-full dark:bg-gray-800 bg-gray-200 text-gray-600 dark:text-gray-300">
                              {timelineItem?.events?.length} events
                            </span>
                          </div>
                          <svg
                            className={`h-4 w-4 transition-transform ${
                              isOpen ? "rotate-180" : ""
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        <div
                          className={`overflow-hidden transition-all ${
                            isOpen ? "max-h-[1000px]" : "max-h-0"
                          }`}
                        >
                          <div className="px-0 pb-0 pt-0">
                            {timelineItem?.events?.length > 0 &&
                              timelineItem?.events?.map((event: any, eventIndex: any) => {
                                // Determine icon and color based on event type
                                let EventIcon = faClipboardList;
                                let accentColor = "#5EEAD4"; // teal by default

                                if (event?.type?.includes("Imaging")) {
                                  EventIcon = faCircleExclamation;
                                  accentColor = "#FFA387"; // peach for imaging
                                }

                                return (
                                  <div
                                    key={`event-${eventIndex}`}
                                    className={`px-4 py-3 ${
                                      eventIndex < timelineItem?.events?.length - 1
                                        ? "border-b dark:border-gray-800/50 border-gray-300"
                                        : ""
                                    } ${
                                      eventIndex % 2 === 0
                                        ? "dark:bg-gray-800/20 bg-gray-300/20"
                                        : "dark:bg-gray-800/10 bg-gray-300/10"
                                    }`}
                                  >
                                    <div className="flex items-start gap-3 mb-2">
                                      <AwesomeIcon
                                        name={EventIcon}
                                        className="h-5 w-5 mt-0.5 text-[#FFA387]"
                                      />
                                      <h3 className="text-normal font-medium dark:text-white text-gray-600">
                                        {event?.type}
                                      </h3>
                                    </div>
                                    <ul className="space-y-2 text-md pl-6">
                                      {event?.items?.length > 0 &&
                                        event?.items?.map((item: any, itemIndex: any) => (
                                          <li
                                            key={`item-${itemIndex}`}
                                            className="dark:text-gray-200 text-gray-500 flex items-start "
                                          >
                                            <span className="mr-2 mt-1 text-[#FFA387]">
                                              •
                                            </span>
                                            <span>{item}</span>
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>

          {/* Footer */}
          <div className="p-4 text-md dark:text-gray-300 text-gray-600 dark:bg-gray-800/50 bg-gray-200 border-t dark:border-gray-800 border-gray-300">
            Please refer to medical guidance for a comprehensive follow-up on AI
            interpretations and treatment contexts for this patient.
          </div>
        </div>
      </div>
    </div>
  );
}
